import React, { useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";

import { useQuery } from "@apollo/client";

import moment from "moment";

import { BORDER_COLOR, TEXT_MUTED, StatusChip, SummaryCard } from "./Shared.component";
import Theme from "../utils/Theme";
import { CSI_REPORT } from "../graphql/query/query";

// Header background (same as Manage User table header)
const HEADER_BG = "#F4F6F8";

// Card shadow used by the Manage User table
const PAPER_SHADOW =
  "0px 0px 2px 0px rgba(0, 171, 85, 0.2),0px 12px 24px -4px rgba(0, 171, 85, 0.12)";

const columns = [
  "Purchased",
  "Customer",
  "Cell",
  "Email",
  "Sales",
  "Initial CSI",
  "Follow-Up CSI",
  "Follow-Up Date",
];

// -----------------------------------------------------------------------------
// Fixed-width truncation
//
// Customer, Cell, Email, and Sales can all hold variable-length data. To
// keep the table's column widths stable no matter how long the underlying
// value is, each of these columns gets a fixed pixel width AND the text
// itself is truncated to a matching character count with a trailing "…".
// Truncating the text (not just relying on CSS ellipsis) means a stray
// long token — e.g. an email with no spaces to wrap on — can never force
// the cell, and therefore the table, to grow.
// -----------------------------------------------------------------------------

const truncate = (value, maxLength = 14) => {
  if (!value) return "—";
  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
};

const CELL_MAX_CHARS = {
  customer: 14,
  cell: 16,
  email: 24,
  sales: 14,
};

const CELL_WIDTH_PX = {
  customer: 120,
  cell: 130,
  email: 170,
  sales: 110,
};

// sx for a cell whose content is truncated to a fixed width. Both width
// and maxWidth are set (not just maxWidth) so the column can't grow past
// this size, and overflow/textOverflow/whiteSpace handle any leftover
// pixel overflow from the font itself.
const truncatedCellSx = (widthKey, extraSx = {}) => ({
  fontSize: "13px",
  width: CELL_WIDTH_PX[widthKey],
  maxWidth: CELL_WIDTH_PX[widthKey],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  ...extraSx,
});

// Grey "N/A" pill for: no response yet on a survey that WAS sent ("Pending").
// Falls back to the shared StatusChip for actual Yes/No values so styling
// stays consistent with the rest of the app.
const CsiStatusCell = ({ value }) => {
  if (value === "Yes") return <StatusChip value="Yes" />;
  if (value === "No") return <StatusChip value="No" />;

  if (value === "Pending") {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          px: 1.2,
          py: 0.3,
          borderRadius: "12px",
          backgroundColor: "#EEF0F2",
          color: TEXT_MUTED,
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        N/A
      </Box>
    );
  }

  // value is null/undefined -> follow-up not due yet, render nothing
  return <Box component="span" sx={{ color: TEXT_MUTED, fontSize: "13px" }}>—</Box>;
};

const formatDate = (value, tz) => {
  if (!value) return "";
  return moment(value).format("MM/DD/YYYY hh:mm A");
};

const CsiTable = ({
  dealershipId,
  employeeUserId,
  isEmployeeOnly,
  dateRange,
  timezone,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, loading, error } = useQuery(CSI_REPORT, {
    skip: !dealershipId,
    fetchPolicy: "network-only",
    variables: {
      reportInput: {
        dealership_id: dealershipId,
        employee_user_id: employeeUserId,
        isEmployeeOnly: !!isEmployeeOnly,
        f_startDate: dateRange?.startDate,
        f_endDate: dateRange?.endDate,
        timezone,
        pageCount: page + 1,
        rowsPerPage,
      },
    },
  });

  const report = data?.csiReport;
  const apiErrors = report?.errors;
  const summaryData = report?.summary;
  const rows = report?.rows || [];
  const total = report?.total || 0;

  const summary = summaryData
    ? [
        { label: "Deliveries", value: String(summaryData.deliveries ?? 0) },
        {
          label: "Initial CSI",
          value: `${summaryData.initialYes ?? 0} / ${summaryData.initialNo ?? 0}`,
        },
        {
          label: "Follow-up CSI",
          value: `${summaryData.followUpYes ?? 0} / ${summaryData.followUpNo ?? 0}`,
        },
        {
          label: "CSI Change",
          value: `${summaryData.csiImproved ?? 0} / ${summaryData.csiDeclined ?? 0}`,
        },
      ]
    : [];

  return (
    <>
      <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", mb: 2 }}>
        {summary.map((s) => (
          <SummaryCard key={s.label} label={s.label} value={s.value} />
        ))}
      </Box>

      {(error || apiErrors?.length > 0) && (
        <Typography sx={{ fontSize: "13px", color: "error.main", mb: 1 }}>
          {error?.message || apiErrors?.[0]?.message || "Unable to load CSI report."}
        </Typography>
      )}

      {/* Table — same card look as the Manage User table */}
      <Paper
        elevation={0}
        sx={{
          boxShadow: PAPER_SHADOW,
          borderRadius: "14px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer
          sx={{
            // Fixed height: pagination stays in the same place whether there
            // are 1 or 100 rows. Rows scroll inside when they don't fit.
            height: "calc(100vh - 430px)",
            minHeight: "250px",
            overflowY: "auto",
            "& .MuiTableCell-root": {
              padding: "8px 12px",
              textAlign: "center",
            },
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      color: "text.primary",
                      backgroundColor: HEADER_BG,
                      whiteSpace: "nowrap",
                      // overflow: "hidden",
                      // textOverflow: "ellipsis",
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ textAlign: "center", py: 4, color: TEXT_MUTED, fontSize: "13px" }}>
                    No CSI records found for this range.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, idx) => (
                  <TableRow hover key={idx}>
                    <TableCell sx={{ fontSize: "13px", whiteSpace: "nowrap" }}>
                      {formatDate(row.purchaseDate)}
                    </TableCell>

                    <TableCell
                      title={row.customer || ""}
                      sx={truncatedCellSx("customer", {
                        color: Theme.palette.primary.main,
                        fontWeight: 600,
                      })}
                    >
                      {truncate(row.customer, CELL_MAX_CHARS.customer)}
                    </TableCell>

                    <TableCell title={row.cell || ""} sx={truncatedCellSx("cell")}>
                      {truncate(row.cell, CELL_MAX_CHARS.cell)}
                    </TableCell>

                    <TableCell title={row.email || ""} sx={truncatedCellSx("email")}>
                      {truncate(row.email, CELL_MAX_CHARS.email)}
                    </TableCell>

                    <TableCell title={row.sales || ""} sx={truncatedCellSx("sales")}>
                      {truncate(row.sales, CELL_MAX_CHARS.sales)}
                    </TableCell>

                    <TableCell><CsiStatusCell value={row.initialCsi} /></TableCell>
                    <TableCell><CsiStatusCell value={row.followUpCsi} /></TableCell>
                    <TableCell sx={{ fontSize: "13px", whiteSpace: "nowrap" }}>
                      {formatDate(row.followUpDate)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "0 8px",
            borderTop: `1px solid ${BORDER_COLOR}`,
            backgroundColor: "#fff",
            "& .MuiTablePagination-toolbar": { minHeight: "40px", padding: 0 },
          }}
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Box>
      </Paper>
    </>
  );
};

export default CsiTable;